/**
 * Created: 2026-10-15
 * Initial commit: Entitlement detail dialog on the admin entitlements page
 *
 * Dialog showing a course entitlement: its basic information, the
 * order that granted it, and the coaching appointments that drew hours from
 * it, with the hours each drew. A super_admin may refund an ACTIVE
 * entitlement's remaining hours, once no still-SCHEDULED appointment holds
 * some of them; the refund itself runs in the background.
 *
 * Updated: 2026-10-15 - Clicking the order ID opens the order dialog
 * Updated: 2026-10-29 - Super admins refund the remaining hours
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { remainingHoursOf } from '@tc-app/shared/course/entitlement'
import {
  COURSE_ENTITLEMENT_STATUS_CHIP,
  formatClassSize,
  SPORT_LABEL,
} from '@tc-app/shared/labels'
import { type ReactNode, useState } from 'react'
import {
  CancelCourseOrderDocument,
  CoachingAttendeeStatus,
  CourseEntitlementDetailDocument,
  CourseEntitlementStatus,
  type CourseEntitlementsQuery,
  SiteRole,
} from '../gql/graphql'
import { useRole } from '../hooks'
import { getWeekdayText } from '../lib/dateUtils'
import OrderDialog from './OrderDialog'
import UserAvatar from './UserAvatar'

gql`
  query CourseEntitlementDetail($id: UUID!) {
    courseEntitlement(id: $id) {
      id
      deductions {
        hours
        attendeeStatus
        appointment {
          sessionId
          coach {
            id
            openid
            name
            avatar
            gender
          }
          slots {
            date
            hour
            court {
              id
              name
            }
          }
        }
      }
    }
  }

  mutation CancelCourseOrder($courseEntitlementId: UUID!) {
    cancelCourseOrder(courseEntitlementId: $courseEntitlementId) {
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

type Entitlement =
  CourseEntitlementsQuery['courseEntitlements']['edges'][number]['node']

const ATTENDEE_STATUS_CHIP: Record<
  CoachingAttendeeStatus,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  [CoachingAttendeeStatus.Scheduled]: { label: '待确认', color: 'warning' },
  [CoachingAttendeeStatus.Confirmed]: { label: '已扣除', color: 'success' },
  [CoachingAttendeeStatus.Cancelled]: { label: '已退回', color: 'default' },
}

const formatHour = (hour: number) => `${String(hour).padStart(2, '0')}:00`

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', width: 72 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  )
}

interface EntitlementDialogProps {
  entitlement: Entitlement | null
  onClose: () => void
  /** Called once a refund has started, after the dialog closes. */
  onRefunded?: () => void
}

export default function EntitlementDialog({
  entitlement,
  onClose,
  onRefunded,
}: EntitlementDialogProps) {
  const [orderOpen, setOrderOpen] = useState(false)
  const { currentSite } = useRole()
  const isSuperAdmin = currentSite?.roles.includes(SiteRole.SuperAdmin) ?? false
  const [cancelCourseOrder] = useMutation(CancelCourseOrderDocument)
  const { data, loading, error, refetch } = useQuery(CourseEntitlementDetailDocument, {
    variables: { id: entitlement?.id ?? '' },
    skip: !entitlement,
    fetchPolicy: 'network-only',
  })

  const deductions = data?.courseEntitlement?.deductions ?? []
  const status = entitlement && COURSE_ENTITLEMENT_STATUS_CHIP[entitlement.status]
  const remainingHours = entitlement ? remainingHoursOf(entitlement) : 0
  // EXPIRED is derived server-side, so an ACTIVE status is also unexpired.
  const canRefund =
    isSuperAdmin &&
    entitlement?.status === CourseEntitlementStatus.Active &&
    remainingHours > 0
  // A SCHEDULED appointment holds hours the refund would hand back, so the
  // backend rejects it; the admin cancels such appointments first. Until the
  // deductions have loaded, whether one exists is unknown.
  const hasScheduled = deductions.some(
    (d) => d.attendeeStatus === CoachingAttendeeStatus.Scheduled,
  )
  const refundBlocked = !data || hasScheduled

  const handleRefund = () => {
    if (!entitlement) return
    message.prompt(
      `将退还剩余的 ${remainingHours} 课时，并按实付金额原路退款给客户。此操作不可撤销。`,
      {
        title: '确认退款',
        noLabel: '返回',
        yesLabel: '确认退款',
        onYesButton: async () => {
          const { data } = await cancelCourseOrder({
            variables: { courseEntitlementId: entitlement.id },
          })
          const result = data?.cancelCourseOrder
          if (result?.__typename === 'GraphqlRequestError') {
            message.error(result.message ?? '退款失败')
            return
          }
          message.success('退款处理中，完成后将短信通知客户', { autoHide: 3000 })
          onClose()
          onRefunded?.()
        },
      },
    )
  }

  return (
    <Dialog open={!!entitlement} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        课时详情
        {status && <Chip label={status.label} color={status.color} size="small" />}
        <Box sx={{ ml: 'auto', display: 'flex' }}>
          <IconButton
            onClick={() => refetch()}
            size="small"
            disabled={loading}
            aria-label="刷新"
          >
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small" aria-label="关闭">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.100', p: 1.5 }}>
        {entitlement && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  基本信息
                </Typography>
                <InfoRow label="客户">
                  <UserAvatar user={entitlement.user} showMobile={true} />
                </InfoRow>
                <InfoRow label="课程">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {entitlement.courseName}
                    </Typography>
                    <Chip label={SPORT_LABEL[entitlement.sport]} size="small" />
                  </Box>
                </InfoRow>
                <InfoRow label="班型">
                  <Typography variant="body2">
                    {formatClassSize(entitlement.seats)}
                  </Typography>
                </InfoRow>
                <InfoRow label="课时">
                  <Typography variant="body2">
                    共 {entitlement.totalHours}，已用 {entitlement.consumedHours}，剩余{' '}
                    {remainingHours}
                  </Typography>
                </InfoRow>
                <InfoRow label="购买时间">
                  <Typography variant="body2">
                    {new Date(entitlement.createTime).toLocaleString('zh-CN')}
                  </Typography>
                </InfoRow>
                <InfoRow label="有效期至">
                  <Typography variant="body2">{entitlement.validUntil}</Typography>
                </InfoRow>
                <InfoRow label="订单号">
                  {entitlement.orderId ? (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setOrderOpen(true)}
                      sx={{
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        textAlign: 'left',
                      }}
                    >
                      {entitlement.orderId}
                    </Link>
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </InfoRow>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                  上课记录
                </Typography>
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                {error && <Alert severity="error">{error.message}</Alert>}
                {!loading && !error && deductions.length === 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    暂无上课记录
                  </Typography>
                )}
                {deductions.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>时间</TableCell>
                        <TableCell>场地</TableCell>
                        <TableCell>教练</TableCell>
                        <TableCell align="center">课时</TableCell>
                        <TableCell align="right">状态</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deductions.map((d) => {
                        const { slots } = d.appointment
                        const dates = [...new Set(slots.map((s) => s.date))]
                        const hours = slots.map((s) => s.hour)
                        const courts = [...new Set(slots.map((s) => s.court.name))]
                        const chip = ATTENDEE_STATUS_CHIP[d.attendeeStatus]
                        return (
                          <TableRow key={d.appointment.sessionId}>
                            <TableCell>
                              {dates.map((date) => (
                                <Box key={date}>
                                  {date} {getWeekdayText(date)}
                                </Box>
                              ))}
                              {hours.length > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'text.secondary' }}
                                >
                                  {formatHour(Math.min(...hours))}-
                                  {formatHour(Math.max(...hours) + 1)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{courts.join('、')}</TableCell>
                            <TableCell>
                              <UserAvatar user={d.appointment.coach} />
                            </TableCell>
                            <TableCell align="center">{d.hours}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={chip.label}
                                color={chip.color}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
      {canRefund && (
        <DialogActions>
          <Tooltip title={hasScheduled ? '有待确认的预约，请先取消后再退款' : ''}>
            {/* A disabled button fires no events; the span shows the tooltip. */}
            <span>
              <Button
                color="error"
                variant="outlined"
                disabled={refundBlocked}
                onClick={handleRefund}
              >
                退款
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      )}
      <OrderDialog
        orderId={orderOpen ? (entitlement?.orderId ?? null) : null}
        onClose={() => setOrderOpen(false)}
      />
    </Dialog>
  )
}
